import React from 'react';

const STOPWORDS = new Set([
  'a', 'an', 'the', 'your', 'my', 'his', 'her', 'our', 'their', 'its',
  'of', 'in', 'on', 'at', 'to', 'for', 'with', 'by', 'up', 'out',
  'i', 'you', 'he', 'she', 'we', 'they', 'it',
  'and', 'or', 'but', 'so', 'nor', 'yet',
  'this', 'that', 'these', 'those',
  'be', 'been', 'being', 'am', 'is', 'are', 'was', 'were',
  'have', 'has', 'had',
  'do', 'does', 'did',
  'get', 'got', 'make', 'made', 'take', 'took',
  'go', 'went', 'come', 'came',
  'not', 'no', 'nor',
]);

function escapeRegex(s: string): string {
  return s.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
}

function extractKeywords(phrase: string): string[] {
  return phrase
    .toLowerCase()
    .split(/\s+/)
    .map(w => w.replace(/[^a-z]/g, ''))
    .filter(w => w.length >= 3 && !STOPWORDS.has(w));
}

export interface WordWithCardId {
  word: string;
  card_id: number | null;
}

export function highlightWordsWithLinks(
  text: string,
  words: WordWithCardId[] | undefined | null,
  onCardClick: (cardId: number) => void
): React.ReactNode[] {
  if (!words?.length) return [text];
  const sorted = [...words].sort((a, b) => b.word.length - a.word.length);
  const patterns = sorted.map(w => {
    const esc = escapeRegex(w.word);
    return w.word.includes(' ') ? `\\b${esc}\\b` : `\\b${esc}\\w*`;
  });
  const regex = new RegExp(`(${patterns.join('|')})`, 'gi');
  const parts = text.split(regex);
  return parts.map((part, i) => {
    if (i % 2 === 1) {
      const matched = sorted.find(w => {
        const esc = escapeRegex(w.word);
        const pat = w.word.includes(' ') ? `\\b${esc}\\b` : `\\b${esc}\\w*`;
        return new RegExp(pat, 'i').test(part);
      });
      if (matched?.card_id != null) {
        return (
          <button key={i} type="button" onClick={() => onCardClick(matched.card_id!)}
            className="font-bold text-indigo-700 hover:text-indigo-900 hover:underline cursor-pointer">
            {part}
          </button>
        );
      }
      return <strong key={i}>{part}</strong>;
    }
    return part;
  });
}

export function highlightWords(text: string, words: string[] | undefined | null): React.ReactNode[] {
  if (!words?.length) return [text];

  const ranges: [number, number][] = [];
  const sorted = [...words].sort((a, b) => b.length - a.length);

  for (const phrase of sorted) {
    // Try exact match first
    const exactRegex = new RegExp(`\\b${escapeRegex(phrase)}\\b`, 'gi');
    let m: RegExpExecArray | null;
    let found = false;
    while ((m = exactRegex.exec(text)) !== null) {
      ranges.push([m.index, m.index + m[0].length]);
      found = true;
    }

    if (!found) {
      // Fuzzy: find content keywords in order within a short window
      const keywords = extractKeywords(phrase);
      if (keywords.length >= 2) {
        const fuzzyPat = keywords
          .map(k => `\\b${escapeRegex(k)}(?:s|es|ed|ing|er|ers)?\\b`)
          .join('[\\s\\S]{0,40}?');
        const fuzzyRegex = new RegExp(fuzzyPat, 'gi');
        while ((m = fuzzyRegex.exec(text)) !== null) {
          ranges.push([m.index, m.index + m[0].length]);
        }
      }
    }
  }

  if (!ranges.length) return [text];

  // Sort and merge overlapping ranges
  ranges.sort((a, b) => a[0] - b[0]);
  const merged: [number, number][] = [];
  for (const [s, e] of ranges) {
    if (merged.length && s <= merged[merged.length - 1][1]) {
      merged[merged.length - 1][1] = Math.max(merged[merged.length - 1][1], e);
    } else {
      merged.push([s, e]);
    }
  }

  // Build React nodes from merged ranges
  const result: React.ReactNode[] = [];
  let cursor = 0;
  for (const [s, e] of merged) {
    if (s > cursor) result.push(text.slice(cursor, s));
    result.push(<strong key={s}>{text.slice(s, e)}</strong>);
    cursor = e;
  }
  if (cursor < text.length) result.push(text.slice(cursor));

  return result;
}
